import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";
import axios from "axios";
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import Alerts from "../../components/Alerts/alerts";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { useTranslation } from "react-i18next";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { uploadFile } from "../../utils/fileUtils";

const PharmacyLedgerDetailTable = ({
  pharmacyId,
  status,
  updatedAt,
  rejectionReason,
  onClose,
  refreshList,
  pharmacyName,
  requestedAmount,
  withDrawlID
}) => {
  const { t } = useTranslation();
  const { authData } = PermissionAccess();
  const [data, setData] = useState([]);
  const [accountDetails, setAccountDetails] = useState([]);
  const [pharmacyBankDetails, setPharmacyBankDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [pageLimit, setPageLimit] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const modalRef = useRef(null);
  const pdfContentRef = useRef(null);
  const [uploadedFilePath, setUploadedFilePath] = useState("");

  const [action, setAction] = useState("");
  const [reason, setReason] = useState(rejectionReason || "");

  const fetchDetails = () => {
    setLoading(true);

    DiscountCouponService.getPharmacyWithDrawlRequestDetails(
      authData.sessionID,
      0,
      100,
      "",
      pharmacyId
    ).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          const cleanData = response.data.data.map(row => {
            const { atteched_file, ...rest } = row;
            return rest;
          });
          setData(cleanData);
          setTotalRows(response.data.count.count);
          setAccountDetails(response.data.accountDetails);
        } else {
          Alerts.swalErrorAlert(t("Unable to fetch details"));
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Server Error, Please try again later."));
      }
    );
  };

  const fetchPharmacyBankDetails = () => {
    if (!pharmacyId) return;

    setBankDetailsLoading(true);
    DiscountCouponService.getPharmacyBankAccountWithDetails(
      authData.sessionID,
      pharmacyId
    ).then(
      (response) => {
        setBankDetailsLoading(false);
        if (response.data.status && response.data.data) {
          setPharmacyBankDetails(response.data.data);
        } else {
          console.log("No pharmacy bank details found");
        }
      },
      (error) => {
        setBankDetailsLoading(false);
        console.log("Error fetching pharmacy bank details:", error);
      }
    );
  };

  useEffect(() => {
    if (status === "Rejected") {
      setAction("Rejected");
      setReason(rejectionReason);
    } else if (status === "Paid") {
      setAction("Approved");
    } else {
      setAction("");
    }
  }, [status, rejectionReason]);

  useEffect(() => {
    if (pharmacyId) {
      fetchDetails();
      fetchPharmacyBankDetails();
    }
  }, [pharmacyId, status, updatedAt]);

  const calculateTotals = () => {
    const credit = data
      .filter(row => row.ladger_action === '+')
      .reduce((sum, row) => sum + (row.pharmacy_actual_amount || 0), 0);
    
    // Only count actual debit transactions, exclude rejected withdrawal requests
    const debit = data
      .filter(row => row.ladger_action === '-' && row.status !== 'Rejected')
      .reduce((sum, row) => sum + (row.pharmacy_actual_amount || 0), 0);
    
    return { credit, debit, balance: credit - debit };
  };

  const totals = calculateTotals();

  const CustomPDFTable = ({ tableData }) => (
    <div style={{
      width: '100%',
      border: '2px solid #3ac47d',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        display: 'flex',
        backgroundColor: '#3ac47d',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        <div style={{ 
          width: '18%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'left'
        }}>
          {t("Date")}
        </div>
        <div style={{ 
          width: '52%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'left'
        }}>
          {t("Description")}
        </div>
        <div style={{ 
          width: '15%', 
          padding: '12px 8px', 
          borderRight: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'center'
        }}>
          {t("Credit (RM)")}
        </div>
        <div style={{ 
          width: '15%', 
          padding: '12px 8px',
          textAlign: 'center'
        }}>
          {t("Debit (RM)")}
        </div>
      </div>

      {tableData.map((row, index) => (
        <div key={index} style={{
          display: 'flex',
          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
          borderBottom: index < tableData.length - 1 ? '1px solid #e9ecef' : 'none',
          minHeight: '40px',
          alignItems: 'center'
        }}>
          <div style={{ 
            width: '18%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            fontSize: '12px',
            fontWeight: '500',
            color: '#495057'
          }}>
            {moment(row.datetime_added).format("DD MMM YYYY")}
          </div>
          <div style={{ 
            width: '52%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            fontSize: '12px',
            lineHeight: '1.3',
            color: '#495057'
          }}>
            {row.required_action}
          </div>
          <div style={{ 
            width: '15%', 
            padding: '8px', 
            borderRight: '1px solid #e9ecef',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {row.ladger_action === '+' ? (
              <span style={{ color: '#3ac47d' }}>
                {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
              </span>
            ) : (
              <span style={{ color: '#6c757d' }}>-</span>
            )}
          </div>
          <div style={{ 
            width: '15%', 
            padding: '8px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {row.ladger_action === '-' && row.status !== 'Rejected' ? (
              <span style={{ color: '#dc3545' }}>
                {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
              </span>
            ) : row.ladger_action === '-' && row.status === 'Rejected' ? (
              <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                {t("Rejected")}
              </span>
            ) : (
              <span style={{ color: '#6c757d' }}>-</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const generatePDFWithWithdrawal = async () => {
    try {
      setPdfExporting(true);

      if (!data || data.length === 0) {
        console.warn("No data available for PDF generation.");
        setPdfExporting(false);
        throw new Error("No transaction data available.");
      }

      const withdrawalTransaction = {
        datetime_added: new Date().toISOString(),
        required_action: `Withdrawal Request - ${pharmacyName}`,
        ladger_action: '-',
        pharmacy_actual_amount: parseFloat(requestedAmount) || 0,
        status: 'Pending' // Add status to distinguish from rejected transactions
      };

      const originalData = [...data];
      setData([withdrawalTransaction, ...originalData]);

      await new Promise(resolve => setTimeout(resolve, 400));

      const modalElement = pdfContentRef.current;
      if (!modalElement) throw new Error("Content not found");

      const pdfContentWidth = 750;
      modalElement.style.width = `${pdfContentWidth}px`;
      modalElement.style.margin = '0 auto';

      await new Promise(resolve => setTimeout(resolve, 200));

      const canvas = await html2canvas(modalElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scrollY: -window.scrollY,
        width: pdfContentWidth,
        height: modalElement.scrollHeight + 100, // Add extra height
        windowWidth: pdfContentWidth,
        windowHeight: modalElement.scrollHeight + 100,
        x: 0,
        y: 0,
        logging: false
      });

      modalElement.style.width = '';
      modalElement.style.margin = '';

      setData(originalData);
      setPdfExporting(false);

      const imgData = canvas.toDataURL('image/png', 0.95);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const margin = 10;
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin);
      
      const scaleToFitWidth = availableWidth / (imgWidth * 0.264583);
      const scaleToFitHeight = availableHeight / (imgHeight * 0.264583);
      const finalScale = Math.min(scaleToFitWidth, scaleToFitHeight);
      
      const finalWidth = (imgWidth * 0.264583) * finalScale;
      const finalHeight = (imgHeight * 0.264583) * finalScale;
      
      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      
      const fileName = `Pharmacy_Ledger_${pharmacyName}_${moment().format('YYYY-MM-DD')}.pdf`;
      pdf.save(fileName);
      
      Alerts.swalSuccessAlert(t("PDF generated successfully"));
    } catch (error) {
      console.error("Error generating PDF:", error);
      setPdfExporting(false);
      Alerts.swalErrorAlert(t("Error generating PDF") + ": " + error.message);
    }
  };

  const handleSubmit = () => {
    if (!action) {
      Alerts.swalErrorAlert(t("Please select an action"));
      return;
    }

    if (action === "Rejected" && !reason.trim()) {
      Alerts.swalErrorAlert(t("Please provide a rejection reason"));
      return;
    }

    setSubmitting(true);

    const statusValue = action === "Approved" ? "Paid" : action;
    
    DiscountCouponService.pharmacyWithdrawlRequestAction(
      authData.sessionID,
      pharmacyId,
      withDrawlID,
      statusValue,
      reason,
      uploadedFilePath
    ).then(
      (response) => {
        setSubmitting(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(t("Action completed successfully"));
          refreshList();
          onClose();
        } else {
          Alerts.swalErrorAlert(response.data.message || t("Action failed"));
        }
      },
      (error) => {
        setSubmitting(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    );
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const path = await uploadFile(file);
      setUploadedFilePath(path);
    } catch (error) {
      Alerts.swalErrorAlert(error.message || t("PDF upload failed."));
    }
  };

  const columns = [
    {
      name: t('Date'),
      selector: row => moment(row.datetime_added).format("DD MMM YYYY"),
      width: "15%",
      sortable: true,
    },
    {
      name: t('Description'),
      selector: row => row.required_action || "-",
      width: "55%",
      sortable: true,
    },
    {
      name: t('Credit'),
      cell: row => (
        row.ladger_action === '+' ? (
          <span style={{ color: '#3ac47d', fontWeight: '600' }}>
            RM {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
          </span>
        ) : (
          <span style={{ color: '#6c757d' }}>-</span>
        )
      ),
      width: "15%",
      sortable: true,
    },
    {
      name: t('Debit'),
      cell: row => (
        row.ladger_action === '-' && row.status !== 'Rejected' ? (
          <span style={{ color: '#dc3545', fontWeight: '600' }}>
            RM {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
          </span>
        ) : row.ladger_action === '-' && row.status === 'Rejected' ? (
          <span style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '12px' }}>
            {t("Rejected")}
          </span>
        ) : (
          <span style={{ color: '#6c757d' }}>-</span>
        )
      ),
      width: "15%",
      sortable: true,
    },
  ];

  return (
    <div ref={pdfContentRef} style={{ backgroundColor: '#ffffff', padding: '20px', borderRadius: '10px' }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '30px',
        borderBottom: '3px solid #3ac47d',
        paddingBottom: '20px'
      }}>
        <h2 style={{ 
          color: '#3ac47d', 
          margin: 0, 
          fontSize: '24px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {t("Pharmacy Ledger Statement")}
        </h2>
        <p style={{ 
          color: '#666', 
          margin: '8px 0 0 0', 
          fontSize: '16px',
          fontWeight: '500'
        }}>
          {pharmacyName}
        </p>
        <p style={{ 
          color: '#888', 
          margin: '4px 0 0 0', 
          fontSize: '14px'
        }}>
          {t("Generated on")}: {moment().format("DD MMMM YYYY, hh:mm A")}
        </p>
      </div>

      {/* Account Details */}
      {accountDetails && accountDetails.length > 0 && (
        <div style={{ 
          marginBottom: '25px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ 
            color: '#3ac47d', 
            marginBottom: '10px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {t("Account Details")}
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {accountDetails.map((account, index) => (
              <div key={index} style={{ 
                flex: '1',
                minWidth: '200px'
              }}>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>{t("Account Name")}:</strong> {account.account_name || "-"}
                </p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>{t("Account Number")}:</strong> {account.bank_account_number || "-"}
                </p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>{t("Bank")}:</strong> {account.bank_details?.name || "-"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Transaction Table */}
      <CustomPDFTable tableData={data} />

      {/* Summary */}
      <div style={{ 
        marginTop: '25px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ 
          color: '#3ac47d', 
          marginBottom: '15px',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          {t("Summary")}
        </h4>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
            <div style={{ 
              color: '#3ac47d', 
              fontSize: '18px', 
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              RM {totals.credit.toFixed(2)}
            </div>
            <div style={{ 
              color: '#666', 
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t("Total Credit")}
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
            <div style={{ 
              color: '#dc3545', 
              fontSize: '18px', 
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              RM {totals.debit.toFixed(2)}
            </div>
            <div style={{ 
              color: '#666', 
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t("Total Debit")}
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: '1', minWidth: '120px' }}>
            <div style={{ 
              color: totals.balance >= 0 ? '#3ac47d' : '#dc3545', 
              fontSize: '18px', 
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              RM {totals.balance.toFixed(2)}
            </div>
            <div style={{ 
              color: '#666', 
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t("Current Balance")}
            </div>
          </div>
        </div>
      </div>

      {/* Action Section */}
      {(status === "Pending" || status === "Rejected") && (
        <div style={{ 
          marginTop: '25px',
          padding: '20px',
          backgroundColor: status === "Pending" ? '#fff3cd' : '#f8d7da',
          borderRadius: '8px',
          border: `1px solid ${status === "Pending" ? '#ffeaa7' : '#f5c6cb'}`
        }}>
          <h4 style={{ 
            color: status === "Pending" ? '#856404' : '#721c24', 
            marginBottom: '15px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {status === "Pending" ? t("Withdrawal Request Action") : t("Edit Withdrawal Request")}
          </h4>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: '600',
              color: '#495057'
            }}>
              {t("Requested Amount")}:
            </label>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#3ac47d',
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e9ecef'
            }}>
              RM {requestedAmount?.toFixed(2) || "0.00"}
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontWeight: '600',
              color: '#495057'
            }}>
              {t("Action")}:
            </label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t("Select Action")}</option>
              <option value="Approved">{t("Approve & Pay")}</option>
              <option value="Rejected">{t("Reject")}</option>
            </select>
          </div>

          {action === "Rejected" && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '600',
                color: '#495057'
              }}>
                {t("Rejection Reason")}:
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                placeholder={status === "Rejected" ? t("Edit rejection reason...") : t("Please provide a reason for rejection...")}
              />
            </div>
          )}

          {action === "Approved" && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '600',
                color: '#495057'
              }}>
                {t("Upload Proof of Payment (PDF)")}:
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff'
                }}
              />
              {uploadedFilePath && (
                <div style={{ 
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#155724'
                }}>
                  ✅ {t("PDF file uploaded successfully")}
                </div>
              )}
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '10px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !action || (action === "Approved" && !uploadedFilePath)}
              style={{
                padding: '10px 20px',
                backgroundColor: action === "Approved" ? '#3ac47d' : action === "Rejected" ? '#dc3545' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (submitting || !action || (action === "Approved" && !uploadedFilePath)) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {submitting ? t("Processing...") : (status === "Rejected" ? t("Update Action") : t("Submit Action"))}
            </button>
          </div>
        </div>
      )}

      {/* Status Display for Completed Actions */}
      {status !== "Pending" && (
        <div style={{ 
          marginTop: '25px',
          padding: '20px',
          backgroundColor: status === "Paid" ? '#d4edda' : '#f8d7da',
          borderRadius: '8px',
          border: `1px solid ${status === "Paid" ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <h4 style={{ 
            color: status === "Paid" ? '#155724' : '#721c24', 
            marginBottom: '15px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {t("Request Status")}
          </h4>
          
          <div style={{ marginBottom: '10px' }}>
            <span style={{ 
              padding: '6px 12px',
              backgroundColor: status === "Paid" ? '#28a745' : '#dc3545',
              color: 'white',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {status}
            </span>
          </div>
          
          {status === "Rejected" && rejectionReason && (
            <div style={{ marginTop: '10px' }}>
              <strong style={{ color: '#721c24' }}>{t("Reason")}:</strong>
              <p style={{ 
                color: '#721c24', 
                margin: '5px 0 0 0',
                fontSize: '14px'
              }}>
                {rejectionReason}
              </p>
            </div>
          )}
          
          {updatedAt && (
            <div style={{ marginTop: '10px' }}>
              <strong style={{ color: status === "Paid" ? '#155724' : '#721c24' }}>
                {t("Updated on")}:
              </strong>
              <span style={{ 
                color: status === "Paid" ? '#155724' : '#721c24',
                marginLeft: '5px',
                fontSize: '14px'
              }}>
                {moment(updatedAt).format("DD MMMM YYYY, hh:mm A")}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Pharmacy Bank Account Details - Loading State */}
      {bankDetailsLoading && (
        <div style={{ 
          marginTop: '25px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <h4 style={{ 
            color: '#3ac47d', 
            marginBottom: '15px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {t("Loading Pharmacy Bank Details")}
          </h4>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <DNALoader />
          </div>
        </div>
      )}

      {/* Pharmacy Bank Account Details - Data Display */}
      {pharmacyBankDetails && !bankDetailsLoading && (
        <div style={{ 
          marginTop: '25px',
          padding: '15px',
          backgroundColor: '#e8f5e8',
          borderRadius: '8px',
          border: '1px solid #c3e6cb'
        }}>
          <h4 style={{ 
            color: '#3ac47d', 
            marginBottom: '10px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {t("Pharmacy Bank Account Details")}
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ 
              flex: '1',
              minWidth: '200px'
            }}>
              <p style={{ margin: '4px 0', fontSize: '14px' }}>
                <strong>{t("Account Name")}:</strong> {pharmacyBankDetails.account_name || "-"}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px' }}>
                <strong>{t("Account Number")}:</strong> {pharmacyBankDetails.bank_account_number || "-"}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px' }}>
                <strong>{t("Bank")}:</strong> {pharmacyBankDetails.bank_details?.name || "-"}
              </p>
              {pharmacyBankDetails.bank_details?.code && (
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>{t("Bank Code")}:</strong> {pharmacyBankDetails.bank_details.code}
                </p>
              )}
              {pharmacyBankDetails.bank_details?.swift_code && (
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>{t("SWIFT Code")}:</strong> {pharmacyBankDetails.bank_details.swift_code}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyLedgerDetailTable;
